import { getSfccImageUrl } from '~/helpers/utils'
import {
  getFilteredFacets,
  mapConstructorFacets,
  mapConstructorProductDataToProductTile,
} from '~/helpers/data-mapping/product'

export type RecommendationRotator = {
  products: Omit<ProductTileProps, 'index'>[] | undefined
  heading?: string
  resultId?: string
  totalProducts: number
}

export const useConstructor = () => {
  const service = constructorIoService()
  const { locale } = useNuxtApp().$i18n
  const { categoryIdToUrl, mhjEnvironment } = useSiteUrl()

  const getPopularSearches = async () => {
    try {
      if ('getPopularSearchTerms' in service) {
        const response = await service.getPopularSearchTerms()
        return response.results?.map((item: ConstructorPopularSearchTerm) => mapPopularSearchTerms(item))
      } else {
        console.error('getPopularSearchTerms service is not available')
        return []
      }
    } catch (e) {
      // Popular searches are a non-critical UI enhancement. If the Constructor request fails, e.g network, ITP,
      // content blocker, bot, etc., fall back to an empty list so the caller doesn't crash Vue's lifecycle handler
      console.error('useConstructor/getPopularSearches', e)
      return []
    }
  }

  const mapPopularSearchTerms = (item: ConstructorPopularSearchTerm) => {
    return {
      contentType: 'link',
      id: item.data.id,
      newTab: false,
      text: item.value,
      url: `${locale.value === 'fr' ? '/fr/' : '/'}search?q=${item.value}`,
      variant: 'text-link',
    }
  }

  const getSearchSuggestions = async (query: string) => {
    try {
      if ('getAutocompleteResults' in service) {
        const userStore = useUserStore()
        const response = await service.getAutocompleteResults(query)
        const imageBaseURL = getSfccImageUrl(mhjEnvironment, true)

        const products = response?.Products?.map((product) => ({
          image: {
            url: imageBaseURL + product?.data?.image_url,
            alt: product?.value,
          },
          productId: product?.data?.id,
          productName: product?.value,
          url: product?.data?.url,
          price: product?.data?.price.amount,
          tieredPrice: { ...product?.data?.price, isFromConstructor: true }, // When present, tieredPrice takes priority over price
          promotions:
            (product?.data?.promotions as ConstructorProductPromotion[])
              ?.filter((promo) =>
                userStore.isLoggedIn ? promo.customerAvailability !== 'guest' : promo.customerAvailability !== 'member',
              )
              .map((promo) => ({
                promotionId: promo.id,
                ...promo,
              })) || [],
        }))

        const searchSuggestions = response?.['Search Suggestions']?.map((word) => {
          const suggestions: any = {
            text: word.value,
            url: `${locale.value === 'fr' ? '/fr/' : '/'}search?q=${word.value}`,
            totalResults: word?.data?.total_num_results ?? undefined,
          }

          return suggestions
        })

        const categories = response?.Categories?.map((category) => {
          const cat: any = {
            name: category.value,
            url: `${categoryIdToUrl(`${locale.value === 'fr' ? '/fr/' : '/'}${category?.data?.id}`)}`,
          }
          const parentCategory = category?.data?.parent
          if (parentCategory && !['root', 'hidden-categories'].includes(parentCategory.id)) {
            cat.parentCategory = parentCategory.displayName
          }
          return cat
        })

        const content = response?.Content?.map((contentItem) => {
          const contentResult: any = {
            name: contentItem?.value,
            url: contentItem?.data?.url,
          }
          return contentResult
        })

        const results: any = {
          products,
          searchSuggestions,
          categories,
          content,
        }
        return results
      } else {
        console.error('getAutocompleteResults service is not available')
        return {}
      }
    } catch (e) {
      // Autocomplete is a non-critical UI enhancement. If Constructor.io is
      // unreachable (network, ITP, content blocker, bot) fall back to an empty
      // result so the search overlay just shows no suggestions.
      console.error('useConstructor/getSearchSuggestions', e)
      return {}
    }
  }

  const getRecommendationRotator = async (recommenderId: string, itemIds?: string[], numResults?: number) => {
    try {
      if (!service?.getRecommendations) {
        throw new Error('Constructor recommendations service is not properly initialized')
      }
      const response = await service.getRecommendations(recommenderId, itemIds, {}, numResults)
      const products = response.response.results?.map((item) =>
        mapConstructorProductDataToProductTile(mhjEnvironment, item),
      )
      return {
        products,
        heading: response.response.pod?.display_name ?? '',
        resultId: response.result_id,
        totalProducts: response.response.total_num_results ?? 0,
      }
    } catch (e) {
      console.error(`useConstructor/getRecommendationRotator`, e)
    }
  }

  const load = async (categorySlug?: string, parameters = {}) => {
    if (!categorySlug) {
      return
    }

    try {
      if (!service?.getBrowseResults) {
        throw new Error('Constructor browse service is not properly initialized')
      }
      const response = await service?.getBrowseResults(categorySlug, parameters)
      const productsResult =
        response?.results?.map((item) => mapConstructorProductDataToProductTile(mhjEnvironment, item)) || []
      const categoriesResult = response?.groups
      const sortOptionsResults = response?.sort_options?.map(mapConstructorSortOptions)
      const facetsResult = {
        data: {
          facets: response?.facets?.map(mapConstructorFacets),
          total: response?.total_num_results,
          filteredFacets: response?.facets && getFilteredFacets(response?.facets),
          perPageOptions: [20, 40, 100],
          itemsPerPage: 22,
          sortOptions: sortOptionsResults,
          products: productsResult,
        },
        input: {
          categorySlug,
          page: 1,
          filters: {},
          itemsPerPage: 22,
        },
      }

      return {
        productsResult,
        facetsResult,
        sortOptionsResults,
        categoriesResult,
      }
    } catch (e) {
      console.error('useConstructor/getCategoryResults', e)
    }
  }

  const search = async (query: string, parameters: ConstructorSearchParameters) => {
    try {
      if (!service?.getSearchResults) {
        throw new Error('Constructor search service is not properly initialized')
      }
      const response = await service?.getSearchResults(query, { ...parameters })

      if (!response?.redirect) {
        const productsResult =
          response?.results?.map((item: any) => mapConstructorProductDataToProductTile(mhjEnvironment, item)) || []
        const categoriesResult = response.groups
        const sortOptionsResults = response.sort_options?.map(mapConstructorSortOptions)
        const facetsResult = {
          data: {
            facets: response.facets?.map(mapConstructorFacets),
            total: response.total_num_results,
            filteredFacets: getFilteredFacets(response.facets),
            perPageOptions: [20, 40, 100],
            itemsPerPage: 22,
            sortOptions: sortOptionsResults.value,
            products: productsResult.value,
          },
          input: {
            page: 1,
            filters: {},
            itemsPerPage: 22,
          },
        }
        const relatedSearches = response.related_searches?.slice(0, 8).map((item: any) => item.query) || []

        return {
          productsResult,
          categoriesResult,
          sortOptionsResults,
          facetsResult,
          relatedSearches,
        }
      }
      return response.redirect
    } catch (e) {
      console.error('useConstructor/getSearchResults', e)
    }
  }

  return {
    getPopularSearches,
    getSearchSuggestions,
    getRecommendationRotator,
    load,
    search,
  }
}
